import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { refreshToken } from '../../../../Redux/Auth/authActions';
import { mapPAEUpdateProjectPayload } from '../Storage/partnerMappers';
import { omitEmptyProjectUpdateFields } from '../Utils/partnerValidation';
import { partnerPortalApi } from '../Services/partnerPortalApi';
import { resolveAccessToken, usePartnerAccessToken } from './usePartnerAccessToken';

const isUnauthorizedError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return message.includes('unauthorized') || message.includes('authentication');
};

export const usePSMProjectUpdate = () => {
  const dispatch = useDispatch();
  const accessToken = usePartnerAccessToken();
  const [updating, setUpdating] = useState(false);

  const withAuthRetry = useCallback(async (apiCall) => {
    const token = resolveAccessToken(accessToken);
    if (!token) {
      throw new Error('Your session has expired. Please sign in again.');
    }

    try {
      return await apiCall(token);
    } catch (err) {
      if (!isUnauthorizedError(err) || !Cookies.get('refreshToken')) {
        throw err;
      }

      await dispatch(refreshToken());
      const refreshedToken = Cookies.get('accessToken');
      if (!refreshedToken) {
        throw new Error('Your session has expired. Please sign in again.');
      }

      return apiCall(refreshedToken);
    }
  }, [accessToken, dispatch]);

  const updateProject = useCallback(async (projectId, payload) => {
    setUpdating(true);

    try {
      return await withAuthRetry((token) => (
        partnerPortalApi.patchProject(
          token,
          projectId,
          omitEmptyProjectUpdateFields(mapPAEUpdateProjectPayload(payload)),
        )
      ));
    } finally {
      setUpdating(false);
    }
  }, [withAuthRetry]);

  return {
    updating,
    updateProject,
  };
};
